/**
 * Geolocation utility functions for requesting and handling user location
 */

export interface UserLocation {
  latitude: number;
  longitude: number;
}

export type LocationPermissionState = 'granted' | 'denied' | 'prompt';

export interface LocationResult {
  location: UserLocation | null;
  permission: LocationPermissionState;
  error: string | null;
}

/**
 * Request user's current location using the Geolocation API
 * Handles permission states and various error scenarios
 * 
 * @returns Promise<LocationResult> containing location data, permission state, and any errors
 */
export async function requestUserLocation(): Promise<LocationResult> {
  // Check if geolocation is available in the browser
  if (!navigator.geolocation) {
    return {
      location: null,
      permission: 'denied',
      error: 'Geolocation is not supported by this browser'
    };
  }

  try {
    // Request current position
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });

    // Successfully obtained location
    return {
      location: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      },
      permission: 'granted',
      error: null
    };
  } catch (error) {
    // Handle geolocation errors
    if (error instanceof GeolocationPositionError) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          return {
            location: null,
            permission: 'denied',
            error: 'User denied location access'
          };
        
        case error.POSITION_UNAVAILABLE:
          return {
            location: null,
            permission: 'prompt',
            error: 'Location information is unavailable'
          };
        
        case error.TIMEOUT:
          return {
            location: null,
            permission: 'prompt',
            error: 'Location request timed out'
          };
        
        default:
          return {
            location: null,
            permission: 'prompt',
            error: 'An unknown error occurred while retrieving location'
          };
      }
    }

    // Handle other types of errors
    return {
      location: null,
      permission: 'prompt',
      error: error instanceof Error ? error.message : 'Failed to get location'
    };
  }
}

/**
 * Check if location permission has been granted without requesting location
 * Uses the Permissions API if available
 * 
 * @returns Promise<LocationPermissionState> current permission state
 */
export async function checkLocationPermission(): Promise<LocationPermissionState> {
  // Check if Permissions API is available
  if (!navigator.permissions || !navigator.permissions.query) {
    return 'prompt';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state as LocationPermissionState;
  } catch (error) {
    // Permissions API not supported or query failed
    return 'prompt';
  }
}
